import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon, CopyIcon, XIcon } from 'lucide-react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { copyText } from '@/lib/clipboard';

type CopyStatus = 'idle' | 'copied' | 'failed';

export function CopyButton({
  value,
  label = 'Copy',
  copiedLabel = 'Copied',
  size = 'sm',
  variant = 'outline',
  className,
}: {
  value: string;
  label?: string;
  copiedLabel?: string;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  className?: string;
}) {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const onClick = useCallback(() => {
    void (async () => {
      const ok = await copyText(value);
      setStatus(ok ? 'copied' : 'failed');
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setStatus('idle'), ok ? 2000 : 4000);
    })();
  }, [value]);

  const Icon = status === 'copied' ? CheckIcon : status === 'failed' ? XIcon : CopyIcon;

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      className={className}
      onClick={onClick}
      aria-live="polite"
    >
      <Icon className="size-4" aria-hidden />
      {status === 'copied' ? copiedLabel : status === 'failed' ? 'Select and copy' : label}
    </Button>
  );
}
